import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Sheet from 'components/core/Sheet';
import Card from 'components/core/Card';
import Button from 'components/core/Button';
import { CircularProgress } from '@mui/joy';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import EvidenceForm from 'components/EvidenceForm';
import evidenceActions from 'actions/evidenceActions';
import observationActions from 'actions/observationActions';
import personActions from 'actions/personActions';

const EvidenceEdit = () => {
    const { evidenceId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [evidence, setEvidence] = useState(null);
    
    const evidences = useSelector(({ evidence }) => evidence.list);
    const currentUser = useSelector(({ auth }) => auth.me);
    const personList = useSelector(({ person }) => person.list);

    useEffect(() => {
        fetchEvidenceData();
        dispatch(evidenceActions.list({ createdBy: currentUser.id }));
        dispatch(observationActions.list());
        dispatch(personActions.list());
    }, [evidenceId, dispatch, currentUser.id]);

    useEffect(() => {
        if (evidence && personList.length > 0) {
            let needsUpdate = false;
            const updates = {};

            // Transform authors for literature
            if (evidence.literature?.authors) {
                const transformedAuthors = evidence.literature.authors.map(person => {
                    if (person.get && person.get('name')) return person;
                    const matchingPersonName = personList.find(pn => pn.get('person').id === person.id);
                    return matchingPersonName || person;
                });
                
                if (transformedAuthors.some(author => author.get && author.get('name'))) {
                    needsUpdate = true;
                    updates.literature = {
                        ...evidence.literature,
                        authors: transformedAuthors
                    };
                }
            }

            // Transform voucherSource for voucher
            if (evidence.voucher?.voucherSource && evidence.voucher.voucherSource.id) {
                // Check if voucherSource is a Person object that needs transformation
                if (!evidence.voucher.voucherSource.get || !evidence.voucher.voucherSource.get('person')) {
                    const matchingPersonName = personList.find(pn => 
                        pn.get('person').id === evidence.voucher.voucherSource.id
                    );
                    if (matchingPersonName) {
                        needsUpdate = true;
                        updates.voucher = {
                            ...evidence.voucher,
                            voucherSource: matchingPersonName
                        };
                    }
                }
            }

            if (needsUpdate) {
                setEvidence(prev => ({
                    ...prev,
                    ...updates
                }));
            }
        }
    }, [evidence, personList]);

    const fetchEvidenceData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await dispatch(evidenceActions.get(evidenceId));
            const rawEvidence = result.value;
            
            const ObservationEvidence = Parse.Object.extend('ObservationEvidence');
            const query = new Parse.Query(ObservationEvidence);
            query.equalTo('evidence', rawEvidence);
            query.include(['observation', 'observation.name', 'observation.name.taxonomy']);
            const observationEvidences = await query.find();
            const linkedObservations = observationEvidences.map(oe => oe.get('observation'));
            
            
            const transformedEvidence = {
                id: rawEvidence.id,
                name: rawEvidence.get('label'),
                evidenceFormat: rawEvidence.get('evidenceFormat'),
                sourceOwned: rawEvidence.get('sourceOwned'),
                subjects: rawEvidence.get('subjects') || [],
                observations: linkedObservations || [],
                voucher: rawEvidence.get('voucher') || {
                    repository: null,
                    catalogNumber: '',
                    additionalNumber: '',
                    voucherSource: null,
                    event: null
                },
                literature: rawEvidence.get('literature') ? {
                    ...rawEvidence.get('literature'),
                    authors: rawEvidence.get('literature').authoredBy || []
                } : {
                    title: '',
                    doi: '',
                    publisher: '',
                    year: '',
                    volume: '',
                    issue: '',
                    startPage: '',
                    endPage: '',
                    authors: [],
                    event: null
                },
                file: rawEvidence.get('file') || {
                    name: '',
                    url: '',
                    event: null
                }
            };
            setEvidence(transformedEvidence);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (savedEvidence) => {
        navigate(`/evidence/${savedEvidence.id}`);
    };

    const handleBack = () => {
        window.history.back();
    };

    if (loading) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <Typography level="h4" color="danger">Error loading evidence</Typography>
                <Typography>{error}</Typography>
            </Stack>
        );
    }

    return (
        <Sheet sx={{ p: 4 }}>
            <Button
                variant="text" 
                onClick={handleBack}
                sx={{ padding: 2 }}
            >
                <ArrowBackIosIcon sx={{ fontSize: 18, marginRight: 1 }} />
                Back
            </Button>
            <Card>
                <Stack spacing={4}>
                    <Typography variant="h4">Edit Evidence</Typography>
                    <EvidenceForm
                        initialEvidence={evidence}
                        evidences={evidences}
                        onSubmit={handleSubmit}
                    />
                </Stack>
            </Card>
        </Sheet>
    );
};

export default EvidenceEdit;